import React, { useCallback, useEffect, useState, useRef } from "react";
import { fetchAjaxListData } from "../../utils";
import { createPortal } from "react-dom";
import { toast } from 'react-toastify';
import { Collapse, Modal } from 'react-bootstrap';
import { Utils, fetchWithAuth, bigAmountFormat, getLocalStorage, filtersObjectHasAnyValue, getPlanDisplayName, getDefaultPageSize, getTotalPages, can, exportListCsv } from '../../utils';
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router";
import '../../App.css'
import ApplicationsFiltersBar from './ApplicationsFiltersBar';
import { MdKeyboardArrowDown, MdAddCircle, MdFileDownload } from 'react-icons/md';
import { FiRefreshCw } from 'react-icons/fi';
import { BsPencil } from "react-icons/bs";
import { RiDashboard3Fill } from "react-icons/ri";
import { RxCross2 } from "react-icons/rx";
import SkeletonLoader from "../common/SkeletonLoader";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";

const EMPTY_APPS_FILTERS = { organizationId: null, search: '' };

const Applications = ({ changeSidebar }) => {
    const [plan, setPlan] = useState("")
    const [appList, setAppList] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [isExporting, setIsExporting] = useState(false);
    const tableScrollRef = useRef(null);
    const tableInnerRef = useRef(null);
    const tableContainerRef = useRef(null);
    const tableElRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    const {
        openActionItem: openItem,
        dropdownPosition,
        dropdownRef,
        closeDropdown,
        handleDropdownAction,
        handleOpenDropdown,
    } = useActionDropdown({
      tableScrollRef,
      optionCount: () => 1 + (can("apps.edit") ? 1 : 0) + (can("apps.delete") ? 1 : 0),
      menuWidth: 200,
    });

    const tableMaxHeight = useTableMaxHeight(tableScrollRef);
    const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, appList?.length);

    const [draftFilters, setDraftFilters] = useState(() => ({ ...EMPTY_APPS_FILTERS }));
    const [appliedFilters, setAppliedFilters] = useState(() => ({ ...EMPTY_APPS_FILTERS }));
    const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [inputValue, setInputValue] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [listRefreshKey, setListRefreshKey] = useState(0);
    const pageSize = getDefaultPageSize();
    const [appsTotal, setAppsTotal] = useState(0);

    // Primary organization shown as a header card above the apps list.
    const [org, setOrg] = useState(null);
    const [showOrgEdit, setShowOrgEdit] = useState(false);
    const [orgNameInput, setOrgNameInput] = useState('');
    const [savingOrg, setSavingOrg] = useState(false);

    useEffect(() => {
        const hasContent =
            filtersObjectHasAnyValue(draftFilters) || filtersObjectHasAnyValue(appliedFilters);
        if (!hasContent) {
            setFilterAccordionOpen(false);
            return;
        }
        setFilterAccordionOpen(true);
    }, [draftFilters, appliedFilters]);

    useEffect(() => {
        const raw = location.state?.orgId;
        const oid = raw !== null && raw !== undefined && raw !== '' ? Number(raw) : null;
        if (Number.isFinite(oid) && oid > 0) {
            setDraftFilters({ ...EMPTY_APPS_FILTERS, organizationId: oid });
            setAppliedFilters({ ...EMPTY_APPS_FILTERS, organizationId: oid });
            setCurrentPage(1);
        }
    }, [location.state?.orgId]);

    const fetchOrganizationOptions = useCallback(({ search, ids }) => {
        const user = JSON.parse(getLocalStorage(Utils.storageKeys.userData) || "null");
        if (!user?.id) return Promise.resolve([]);
        return fetchAjaxListData({
            type: "organizations",
            q: search,
            id: ids,
            extra: { userId: user.id },
            mode: "options",
        });
    }, []);

    useEffect(() => {
        const user = JSON.parse(getLocalStorage(Utils.storageKeys.userData));
        if (!user) return;
        setIsLoading(true);
        const searchTrim = (appliedFilters.search || '').trim();
        fetchWithAuth(Utils.baseurl + 'getall/application', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId: user.id,
                page: currentPage,
                pageSize,
                ...(appliedFilters.organizationId
                    ? { organizationId: Number(appliedFilters.organizationId) }
                    : {}),
                ...(searchTrim ? { search: searchTrim } : {}),
            }),
        })
            .then((response) => response.json())
            .then((result) => {
                if (result.status) {
                    setAppList(result.data || []);
                    setAppsTotal(result.meta?.total ?? (result.data?.length || 0));
                } else {
                    toast.error(result.message || 'Failed to fetch apps');
                    setAppList([]);
                    setAppsTotal(0);
                }
            })
            .catch(() => {
                toast.error('Request failed');
                setAppList([]);
                setAppsTotal(0);
            })
            .finally(() => setIsLoading(false));
    }, [currentPage, pageSize, appliedFilters.organizationId, appliedFilters.search, listRefreshKey]);

    const handleApplyFilters = () => {
        setAppliedFilters({ ...draftFilters });
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        const cleared = { ...EMPTY_APPS_FILTERS };
        setDraftFilters(cleared);
        setAppliedFilters(cleared);
        setCurrentPage(1);
        navigate(location.pathname, { replace: true, state: {} });
    };

    const totalRows = appsTotal || 0;
    const totalPages = getTotalPages(totalRows, pageSize);
    const safePage = Math.min(Math.max(1, currentPage), totalPages);


    const handleOpenModal = (id, name) => {
        closeDropdown();
        setInputValue(name);
        setSelectedRowId(id);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setInputValue('');
    };

    const handleSubmit = () => {
        if (inputValue.length) {

            fetchWithAuth(Utils.baseurl + 'update/application', {
                method: 'POST',
                body: JSON.stringify({ application_name: inputValue, id: selectedRowId }),
            })
                .then((response) => response.json())
                .then(async (result) => {
                    if (result.status) {
                        handleCallRenderList()
                        handleCloseModal();
                    }
                })
                .catch((error) => console.error(error));
        }


    };







    async function handleCallRenderList() {
        const user = JSON.parse(getLocalStorage(Utils.storageKeys.userData));

        if (user) {

            fetchWithAuth(Utils.baseurl + 'user/getbyid', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ userId: user.id }),
            })
                .then((response) => response.json())
                .then(async (result) => {
                    if (result.status) {
                        const planType = result.data?.sub?.plan_type;
                        setPlan(planType ? getPlanDisplayName(planType) : "Free");
                    } else {
                        toast.error(result.message)
                    }
                })
                .catch((error) => console.error(error));




            const searchTrim = (appliedFilters.search || '').trim();
            fetchWithAuth(Utils.baseurl + 'getall/application', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: user.id,
                    page: currentPage,
                    pageSize,
                    ...(appliedFilters.organizationId
                        ? { organizationId: Number(appliedFilters.organizationId) }
                        : {}),
                    ...(searchTrim ? { search: searchTrim } : {}),
                }),
            })
                .then((response) => response.json())
                .then((result) => {
                    if (result.status) {
                        setIsLoading(false);
                        setAppList(result.data || []);
                        setAppsTotal(result.meta?.total ?? (result.data?.length || 0));
                    } else if (result.errors) {
                        // Handle server-side validation errors (optional)
                    }
                })
                .catch((error) => console.error(error));
        }
    }

    useEffect(() => {
        const user = JSON.parse(getLocalStorage(Utils.storageKeys.userData));

        if (user) {

            fetchWithAuth(Utils.baseurl + 'user/getbyid', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ userId: user.id }),
            })
                .then((response) => response.json())
                .then(async (result) => {
                    if (result.status) {
                        const planType = result.data?.sub?.plan_type;
                        setPlan(planType ? getPlanDisplayName(planType) : "Free");
                    } else {
                        toast.error(result.message)
                    }
                })
                .catch((error) => console.error(error));




        }
    }, []);

    // Load the user's primary organization for the header card.
    const fetchPrimaryOrg = useCallback(() => {
        const user = JSON.parse(getLocalStorage(Utils.storageKeys.userData) || "null");
        if (!user?.id) return;
        fetchWithAuth(Utils.baseurl + 'get-organizations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: 1, pageSize: 1 }),
        })
            .then((r) => r.json())
            .then((result) => {
                if (result.status && Array.isArray(result.data) && result.data.length) {
                    setOrg(result.data[0]);
                }
            })
            .catch(() => { /* header is best-effort */ });
    }, []);

    useEffect(() => {
        fetchPrimaryOrg();
    }, [fetchPrimaryOrg]);

    const openOrgEdit = () => {
        setOrgNameInput(org?.oganization_name || '');
        setShowOrgEdit(true);
    };

    const handleOrgEditSubmit = async () => {
        const name = String(orgNameInput || '').trim();
        if (!org?.id) return;
        if (!name) {
            toast.error('Organization name is required');
            return;
        }
        const user = JSON.parse(getLocalStorage(Utils.storageKeys.userData) || "null");
        setSavingOrg(true);
        try {
            const res = await fetch(Utils.baseurl + 'update-organization', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ organizationId: org.id, userId: user?.id, oganization_name: name }),
            });
            const result = await res.json();
            if (result.status) {
                toast.success(result.message || 'Organization updated');
                setShowOrgEdit(false);
                setOrg((prev) => (prev ? { ...prev, oganization_name: name } : prev));
            } else {
                toast.error(result.message || 'Failed to update organization');
            }
        } catch (e) {
            toast.error('Network error');
        } finally {
            setSavingOrg(false);
        }
    };

    function handleAction(type, id) {

        if (type === "Delete") {
            closeDropdown();
            // /delete/application

            fetchWithAuth(Utils.baseurl + 'delete/application', {
                method: 'POST',
                body: JSON.stringify({ id: id }),
            })
                .then((response) => response.json())
                .then(async (result) => {
                    if (result.status) {

                        handleCallRenderList()
                        toast.success(result.message)
                    }
                })
                .catch((error) => console.error(error));
        }

        if (type === "Open Dashboard") {
            navigate(`/apps/${id}`)
            changeSidebar('dashboardSidebar')
        }

    }


    useEffect(() => {
        changeSidebar('defaultSidebar', 0)
        localStorage.setItem("thirdSidebar", 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleModal = () => { // eslint-disable-line no-unused-vars
        setShowModal(!showModal);
    };



    let loaderData = [
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        }
    ]
    function capitalize(str) {
        if (!str) return "";
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    const handleExport = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const user = JSON.parse(getLocalStorage(Utils.storageKeys.userData));
            const searchTrim = (appliedFilters.search || "").trim();
            const { status, message } = await exportListCsv("getall/application", {
                userId: user?.id,
                ...(appliedFilters.organizationId
                    ? { organizationId: Number(appliedFilters.organizationId) }
                    : {}),
                ...(searchTrim ? { search: searchTrim } : {}),
            }, "apps.csv");
            if (!status) {
                toast.error(message);
                return;
            }
            if (message) toast.info(message);
        } catch (error) {
            console.error(error);
            toast.error("Export failed");
        } finally {
            setIsExporting(false);
        }
    };


    return (
        <div className="dashboard_right ns-support-list">
            <div className="dashboard_inner">
                {org && (
                    <div className="org-header-card">
                        <div className="org-header-top">
                            <div className="org-header-info">
                                <span className="org-header-label">Organization</span>
                                <h2 className="org-header-name">{capitalize(org.oganization_name || 'N/A')}</h2>
                            </div>
                            <div className="org-header-side">
                                {plan ? <span className="org-header-plan">{plan}</span> : null}
                                {can("apps.edit") ? (
                                <button
                                    type="button"
                                    className="org-header-edit"
                                    onClick={openOrgEdit}
                                    title="Edit organization"
                                    aria-label="Edit organization"
                                >
                                    <BsPencil size={16} />
                                </button>
                                ) : null}
                            </div>
                        </div>
                        <div className="org-header-stats">
                            <div className="org-stat">
                                <span className="org-stat-value">{bigAmountFormat(org.mau_count ?? 0)}</span>
                                <span className="org-stat-label">Mobile (MAU)</span>
                            </div>
                            <div className="org-stat">
                                <span className="org-stat-value">{bigAmountFormat(org.web_push_subscribers_count ?? 0)}</span>
                                <span className="org-stat-label">Web Push Subscribers</span>
                            </div>
                            <div className="org-stat">
                                <span className="org-stat-value">{bigAmountFormat(org.app_users_count ?? 0)}</span>
                                <span className="org-stat-label">Total Records</span>
                            </div>
                            <div className="org-stat">
                                <span className="org-stat-value">{bigAmountFormat(org.subscriptions_active_count ?? 0)}</span>
                                <span className="org-stat-label">Total Subscribed</span>
                            </div>
                            <div className="org-stat">
                                <span className="org-stat-value">{bigAmountFormat(org.applications?.length ?? 0)}</span>
                                <span className="org-stat-label">Apps</span>
                            </div>
                        </div>
                    </div>
                )}
                <div className="dashboard_right_top">
                    <div className="dashboard_heading">
                        <h1>All Apps</h1>
                    </div>
                    <div className="add_app" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button type="button" className="add_app_btn_secondary btn_theme_secondary d-none" title="Refresh list" onClick={() => setListRefreshKey((n) => n + 1)}>
                            <FiRefreshCw />
                            <span>Refresh</span>
                        </button>
                        <button type="button" className="add_app_btn_secondary btn_theme_secondary d-none" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
                            <MdFileDownload />
                            <span>{isExporting ? 'Exporting…' : 'Export'}</span>
                        </button>
                        {can("apps.add") ? (
                        <Link to="/apps/new" className="add_app_btn btn_theme_primary">
                            <MdAddCircle />
                            <span>New App/Website</span>
                        </Link>
                        ) : null}
                    </div>
                </div>

                <div className="list-page-filter-card">
                    <button
                        type="button"
                        className="list-page-filter-accordion-header"
                        onClick={() => setFilterAccordionOpen((o) => !o)}
                        aria-expanded={filterAccordionOpen}
                        aria-controls="all-apps-filter-panel"
                        id="all-apps-filter-accordion"
                    >
                        <span className="list-page-filter-accordion-title">Filter</span>
                        <MdKeyboardArrowDown
                            className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? 'list-page-filter-accordion-chevron--open' : ''}`}
                            size={22}
                            aria-hidden
                        />
                    </button>
                    <Collapse in={filterAccordionOpen}>
                        <div
                            className="list-page-filter-accordion-body"
                            id="all-apps-filter-panel"
                            role="region"
                            aria-labelledby="all-apps-filter-accordion"
                        >
                            <ApplicationsFiltersBar
                                draftFilters={draftFilters}
                                setDraftFilters={setDraftFilters}
                                fetchOrganizationOptions={fetchOrganizationOptions}
                                onSearch={handleApplyFilters}
                                onReset={handleResetFilters}
                            />
                        </div>
                    </Collapse>
                </div>

                <div ref={tableContainerRef} className="dashboard_app_table">
                    <div className="dashboard_table">
                        <div
                            ref={tableScrollRef}
                            className="table_scroll_wrapper"
                        >
                            <div
                                ref={tableInnerRef}
                                className={`table_inner table_y_scroll ${isAtScrollEnd ? 'at-scroll-end' : ''}`}
                                style={{
                                    maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : 'calc(100vh - 320px)',
                                    overflowY: 'auto',
                                    overflowX: 'auto',
                                }}
                            >
                                <table ref={tableElRef} className="table_outer table-sm">
                                    <thead>
                                        <tr>
                                            <th >Name</th>
                                            <th >Organization Name</th>
                                            <th style={{ textAlign: "center" }}>Plan</th>
                                            <th style={{ textAlign: "center" }} >Mobile (MAU)</th>
                                            <th style={{ textAlign: "center" }} >Web Push Subscribers</th>
                                            <th style={{ textAlign: "center" }} >Total Records</th>
                                            <th style={{ textAlign: "center" }} >Total Subscribed</th>
                                            <th>Platforms</th>
                                            <th className="col-action" style={{ textAlign: "center" }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {appList.length === 0 ? (
                                            <tr>
                                                <td colSpan="9" style={{ textAlign: "center" }}>
                                                    {
                                                        isLoading ?
                                                            // <FontAwesomeIcon icon={faSpinner} style={{ marginTop: "10px" }} spin size="2x" />
                                                            <SkeletonLoader columns={loaderData} rows={2} />
                                                            : <div style={{ padding: "40px 20px" }}>
                                                                <p style={{ fontSize: 20, fontWeight: 500, marginBottom: "10px" }} >No applications found</p>
                                                                <p style={{ fontSize: 14, color: "#666", marginBottom: "20px" }}>Create your first app to get started</p>
                                                                {can("apps.add") ? (
                                                                <Link to="/apps/new" style={{
                                                                    display: "inline-block",
                                                                    padding: "10px 20px",
                                                                    background: "#4F46E5",
                                                                    color: "white",
                                                                    borderRadius: "6px",
                                                                    textDecoration: "none",
                                                                    fontWeight: 500
                                                                }}>
                                                                    Create App
                                                                </Link>
                                                                ) : null}
                                                            </div>
                                                    }
                                                </td>
                                            </tr>
                                        ) : appList.map(item => (
                                            <tr
                                                key={item.id}
                                                className="support-ticket-row--clickable"
                                                onClick={() => handleAction('Open Dashboard', item.app_id)}
                                            >
                                                <td style={{ fontWeight: 600 }}>
                                                    {capitalize(item.application_name)}
                                                </td>
                                                <td>
                                                    {capitalize(item.organization?.oganization_name || 'N/A')}
                                                </td>
                                                <td style={{ textAlign: "center" }}><span className="ns-pill">{plan}</span></td>
                                                <td style={{ textAlign: "center" }}>{bigAmountFormat(item.mau_count ?? 0)}</td>
                                                <td style={{ textAlign: "center" }}>{bigAmountFormat(item.web_push_subscribers_count ?? 0)}</td>
                                                <td style={{ textAlign: "center" }}>{bigAmountFormat(item.app_users_count ?? 0)}</td>
                                                <td style={{ textAlign: "center" }}>{bigAmountFormat(item.subscriptions_active_count ?? 0)}</td>
                                                <td style={{ textAlign: "left" }}>
                                                    <>
                                                        {

                                                            item.applicationDetails?.length ? item.applicationDetails.map(application => (
                                                                application.json_name && Number(application.channel_type) === 2 ?
                                                                    <svg key={application.id} style={{ height: "16px", width: "20%" }} viewBox="0 0 1274.37 718" fill="currentColor" className="sc-jSMfEi hKsxtk"><path d="m930.77 536.42a53.07 53.07 0 1 1 53.06-53.08 53.14 53.14 0 0 1 -53.06 53.08m-586.54 0a53.07 53.07 0 1 1 53.06-53.08 53.13 53.13 0 0 1 -53.06 53.08m605.57-319.65 106.05-183.68a22.06 22.06 0 1 0 -38.21-22.09l-107.39 186c-82.12-37.48-174.35-58.35-272.76-58.35s-190.63 20.9-272.75 58.35l-107.38-186a22.06 22.06 0 1 0 -38.22 22.06l106.06 183.71c-182.11 99.05-306.67 283.41-324.89 501.23h1274.38c-18.24-217.82-142.79-402.18-324.89-501.23" fill="#3ddc84"></path></svg>
                                                                    : application.json_name && Number(application.channel_type) === 1 ?
                                                                        <svg key={application.id} style={{ height: "20px", width: "40%" }} viewBox="0 0 48 48" fill="currentColor"><g fill="#000"><path d="M33.051.057s-3.56.087-6.825 3.351c-3.266 3.265-2.715 7.663-2.715 7.663s3.232.592 6.682-2.858S33.05.057 33.05.057zM23.885 13.856c-2.407-.017-3.886-2.095-7.984-2.095-3.688 0-7.385 2.38-8.957 4.682-1.565 2.292-2.92 4.805-2.92 9.906 0 5.1 2.457 13.872 8.495 19.81 1.034 1.018 2.403 1.782 3.966 1.83 2.809.084 4.534-2.018 8.082-2.018 3.548 0 4.616 2.017 7.819 2.017 1.457 0 3.117-.663 4.73-2.38 2.07-2.2 4.64-6.393 5.947-10.414 0 0-6.551-2.53-6.473-9.824.066-6.045 5.2-9.018 5.2-9.018s-2.999-4.78-9.372-4.78c-4.401.033-6.373 2.299-8.533 2.284z"></path></g></svg>
                                                                        : "Continue Setup"
                                                            )) : "Continue Setup"
                                                        }
                                                    </>

                                                </td>
                                                <td className="col-action" style={{ position: "relative", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                                    <button type="button" className="tabButton dropdown-trigger-btn" onClick={(e) => { e.stopPropagation(); handleOpenDropdown(e, item.id, item); }}>
                                                        <svg viewBox="-4 0 16 16" fill="currentColor">
                                                            <path d="M8 5.25A1.625 1.625 0 108 2a1.625 1.625 0 000 3.25zM8 9.625a1.625 1.625 0 100-3.25 1.625 1.625 0 000 3.25zM9.625 12.375a1.625 1.625 0 11-3.25 0 1.625 1.625 0 013.25 0z" fill="currentColor">
                                                            </path>
                                                        </svg>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                            // : (
                                            // <tr>
                                            //     <td colSpan="9" style={{ textAlign: "center" }}></td>
                                            // </tr>
                                            //   )
                                        }
                                        {/* <tr>
                                    <td>
                                        <a href="#">kashfiya</a>
                                    </td>
                                    <td className="plan"><span>Free</span></td>
                                    <td className="text-end">0</td>
                                    <td className="text-end">0</td>
                                    <td className="text-end"><span>0</span> &nbsp;(+0%)</td>
                                    <td className="text-end"><span>0</span></td>
                                    <td>
                                        <svg viewBox="0 0 1274.37 718"  fill="currentColor" className="sc-jSMfEi kGzldm sc-dzVsgF MkLrn platform-icon">
                                            <path d="m930.77 536.42a53.07 53.07 0 1 1 53.06-53.08 53.14 53.14 0 0 1 -53.06 53.08m-586.54 0a53.07 53.07 0 1 1 53.06-53.08 53.13 53.13 0 0 1 -53.06 53.08m605.57-319.65 106.05-183.68a22.06 22.06 0 1 0 -38.21-22.09l-107.39 186c-82.12-37.48-174.35-58.35-272.76-58.35s-190.63 20.9-272.75 58.35l-107.38-186a22.06 22.06 0 1 0 -38.22 22.06l106.06 183.71c-182.11 99.05-306.67 283.41-324.89 501.23h1274.38c-18.24-217.82-142.79-402.18-324.89-501.23" fill="#3ddc84">
                                            </path>
                                        </svg>
                                    </td>
                                    <td>
                                        <button type="button">
                                            <svg viewBox="0 0 16 16"  fill="currentColor">
                                                <path d="M8 5.25A1.625 1.625 0 108 2a1.625 1.625 0 000 3.25zM8 9.625a1.625 1.625 0 100-3.25 1.625 1.625 0 000 3.25zM9.625 12.375a1.625 1.625 0 11-3.25 0 1.625 1.625 0 013.25 0z" fill="currentColor">
                                                </path>
                                            </svg>
                                        </button>
                                    </td>
                                </tr> */}
                                    </tbody>
                                </table>
                            </div>
                            <ListPaginationBar
                                page={safePage}
                                setPage={setCurrentPage}
                                total={totalRows}
                                pageSize={pageSize}
                                totalPages={totalPages}
                                ariaLabel="Apps pagination"
                                loading={isLoading}
                            />
                            {/* Modal */}
                            <Modal show={showModal} onHide={handleCloseModal} className="ns-modal">
                                <Modal.Header closeButton>
                                    <Modal.Title> <b> Rename App</b> </Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <div style={{ margin: "5px", color: "#44464d" }} > New App Name</div>
                                    <input
                                        type="text"
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        className="form-control"
                                    />
                                </Modal.Body>
                                <Modal.Footer>
                                    <button type="button" className="btn_theme_secondary" onClick={handleCloseModal}>
                                        Cancel
                                    </button>
                                    <button type="button" className="add_app_btn btn_theme_primary" onClick={handleSubmit}>
                                        <span>Rename</span>
                                    </button>
                                </Modal.Footer>
                            </Modal>
                        </div>
                    </div>
                </div>
            </div>
            {/* Edit Organization Modal */}
            <Modal show={showOrgEdit} onHide={() => !savingOrg && setShowOrgEdit(false)} centered className="ns-modal">
                <Modal.Header closeButton>
                    <Modal.Title> <b>Edit Organization</b> </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ margin: "5px", color: "#44464d" }}>Organization Name</div>
                    <input
                        type="text"
                        value={orgNameInput}
                        onChange={(e) => setOrgNameInput(e.target.value)}
                        className="form-control"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn_theme_secondary" onClick={() => !savingOrg && setShowOrgEdit(false)}>
                        Cancel
                    </button>
                    <button type="button" className="add_app_btn btn_theme_primary" onClick={handleOrgEditSubmit} disabled={savingOrg}>
                        <span>{savingOrg ? "Saving…" : "Save"}</span>
                    </button>
                </Modal.Footer>
            </Modal>
            {openItem && dropdownPosition && createPortal(
                <div ref={dropdownRef} style={{ position: 'fixed', left: dropdownPosition.left, top: dropdownPosition.top, zIndex: 10000, minWidth: '220px' }}>
                    <div className="dropdown-menu-subscription">
                        <button onClick={() => handleDropdownAction(() => handleAction('Open Dashboard', openItem.app_id))} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}>
                            <RiDashboard3Fill size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>Open Dashboard</span>
                        </button>
                        {can("apps.edit") ? (
                        <button onClick={() => handleOpenModal(openItem.id, openItem.application_name)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}>
                            <BsPencil size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>Rename</span>
                        </button>
                        ) : null}
                        {can("apps.delete") ? (
                        <button className="delete-class" onClick={() => handleDropdownAction(() => handleAction('Delete', openItem.id))} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', color: 'red' }}>
                            <RxCross2 size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>Delete</span>
                        </button>
                        ) : null}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Applications;
